import Phaser from 'phaser';

/**
 * Летающий по диагонали жук. Не подвержен гравитации. При столкновении со
 * стенами/полом/потолком/платформами перевёрстывает направление (физика
 * `setBounce(1, 1)` делает это автоматически на коллайдере).
 */
export class EnemyFloater extends Phaser.Physics.Arcade.Sprite {
  private hp = 12;
  private hurtTimer = 0;
  private dying = false;

  private bounds?: { left: number; right: number; top: number; bottom: number };

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    vx = 64,
    vy = 64,
    bounds?: { left: number; right: number; top: number; bottom: number },
  ) {
    super(scene, x, y, 'enemy-floater-hq');
    scene.add.existing(this);
    scene.physics.add.existing(this);
    // Размер сопоставим с героем (80×104). HQ-painterly спрайт на полупрозрачном
    // фоне — реальный визуал занимает ~70% canvas, поэтому ставим больший
    // displaySize для правильного восприятия.
    this.setDisplaySize(96, 84);

    this.bounds = bounds;

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(60, 50);
    body.setAllowGravity(false);
    // Phaser-bounce работает с коллайдерами: для world bounds (если bounds
    // не задан) и для коллайдера с террейном (платформы, стены) —
    // регистрируется в addFloater. Это Xonix-стилевый отскок: компоненты
    // velocity переворачиваются на стенах, угол сохраняется (45° остаётся 45°).
    body.setBounce(1, 1);
    if (!bounds) {
      body.setCollideWorldBounds(true);
    }
    body.setVelocity(vx, vy);
    body.setMaxVelocity(180, 180);
    // Без wobble-tween — Xonix-шарик летит ровно
  }

  override preUpdate(time: number, delta: number) {
    super.preUpdate(time, delta);
    if (this.dying) return;
    this.hurtTimer = Math.max(0, this.hurtTimer - delta);

    const body = this.body as Phaser.Physics.Arcade.Body;

    // Ручной рикошет по своему патрульному прямоугольнику
    if (this.bounds) {
      if (this.x <= this.bounds.left && body.velocity.x < 0) {
        body.setVelocityX(Math.abs(body.velocity.x));
        this.x = this.bounds.left;
      }
      if (this.x >= this.bounds.right && body.velocity.x > 0) {
        body.setVelocityX(-Math.abs(body.velocity.x));
        this.x = this.bounds.right;
      }
      if (this.y <= this.bounds.top && body.velocity.y < 0) {
        body.setVelocityY(Math.abs(body.velocity.y));
        this.y = this.bounds.top;
      }
      if (this.y >= this.bounds.bottom && body.velocity.y > 0) {
        body.setVelocityY(-Math.abs(body.velocity.y));
        this.y = this.bounds.bottom;
      }
    }

    // Защита от затухания скорости (численная погрешность bounce)
    if (Math.abs(body.velocity.x) < 30) {
      body.setVelocityX(body.velocity.x >= 0 ? 60 : -60);
    }
    if (Math.abs(body.velocity.y) < 30) {
      body.setVelocityY(body.velocity.y >= 0 ? 60 : -60);
    }
    this.setFlipX(body.velocity.x < 0);
  }

  takeDamage(amount: number, knockbackX: number) {
    if (this.dying) return;
    this.hp -= amount;
    this.hurtTimer = 200;

    this.setTintFill(0xffffff);
    this.scene.time.delayedCall(80, () => {
      if (this.active && !this.dying) this.clearTint();
    });

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocityX(knockbackX);

    if (this.hp <= 0) this.die();
  }

  private die() {
    this.dying = true;
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setEnable(false);

    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scaleX: 0.5,
      scaleY: 0.5,
      duration: 280,
      onComplete: () => this.destroy(),
    });
  }

  isAlive() {
    return !this.dying && this.active;
  }
}
